import { useEffect, useRef } from 'react';
import { useRemoteAudio } from '@huddle01/react/hooks';
import type { PeerAudioState } from '../types';

interface PeerAudioProps {
  peerId: string;
  audioState: PeerAudioState;
  selectedOutputDeviceId?: string;
  onStateChange?: (peerId: string, state: string, isAudioOn: boolean) => void;
}

/**
 * Audio element for a single remote peer with proximity-based volume
 */
export function PeerAudio({ peerId, audioState, selectedOutputDeviceId, onStateChange }: PeerAudioProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  
  const { stream, track, state, isAudioOn } = useRemoteAudio({
    peerId,
    onPlayable: (data) => {
      console.log(`[ProxChat] Audio PLAYABLE for ${audioState.name}`, {
        peerId,
        hasStream: !!data.stream,
        hasTrack: !!data.track,
      });
    },
    onClose: (reason) => {
      console.log(`[ProxChat] Audio CLOSED for ${audioState.name}`, reason);
    },
  });

  // Log state changes for debugging
  useEffect(() => {
    console.log(`[ProxChat] Remote audio state for ${audioState.name}:`, {
      peerId,
      state,
      isAudioOn,
      hasStream: !!stream,
      hasTrack: !!track,
      volume: audioState.volume,
    });
    onStateChange?.(peerId, state, isAudioOn);
  }, [peerId, state, isAudioOn, stream, track, audioState.name, audioState.volume, onStateChange]);

  // Attach stream to audio element manually
  useEffect(() => {
    const audioEl = audioRef.current;
    if (audioEl && stream) {
      console.log(`[ProxChat] Attaching stream to audio element for ${peerId}`);
      audioEl.srcObject = stream;
      
      // Explicitly try to play
      audioEl.play().catch(e => {
        console.error(`[ProxChat] Autoplay failed for ${peerId}:`, e);
      });
    } else if (audioEl && !stream) {
        audioEl.srcObject = null;
    }
  }, [stream, peerId]);

  // Apply proximity-based volume to our audio ref
  useEffect(() => {
    if (audioRef.current) {
      const clampedVolume = Math.max(0, Math.min(1, audioState.volume));
      audioRef.current.volume = clampedVolume;
      console.log(`[ProxChat] Set volume for ${audioState.name}: ${(clampedVolume * 100).toFixed(0)}%`);
    }
  }, [audioState.volume, audioState.name]);

  // Apply selected audio output device (if supported)
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (!selectedOutputDeviceId) return;

    const maybeSetSinkId = (audioEl as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId;
    if (!maybeSetSinkId) {
      return;
    }

    maybeSetSinkId.call(audioEl, selectedOutputDeviceId).catch((e: unknown) => {
      console.warn('[ProxChat] Failed to set audio output device:', e);
    });
  }, [selectedOutputDeviceId]);

  return (
    <audio
      ref={audioRef}
      autoPlay
      playsInline
      controls={false} // Hidden, but we manage playback
      style={{ display: 'none' }}
      data-peer-id={peerId}
      data-state={state}
    />
  );
}

interface PeerAudioListProps {
  peerAudioStates: PeerAudioState[];
  /** All peer IDs from Huddle01 (may include peers not yet in peerAudioStates) */
  allPeerIds?: string[];
  onPeerStateChange?: (peerId: string, state: string, isAudioOn: boolean) => void;
}

/**
 * Renders audio elements for all remote peers
 * Now renders for ALL huddle peers, not just those with position data
 */
export function PeerAudioList({ peerAudioStates, allPeerIds = [], onPeerStateChange, selectedOutputDeviceId }: PeerAudioListProps & { selectedOutputDeviceId?: string }) {
  // Create a map of known audio states
  const audioStateMap = new Map(peerAudioStates.map(s => [s.peerId, s]));
  
  // Merge: all peerIds from Huddle + any in audioStates
  const allPeers = new Set([...allPeerIds, ...peerAudioStates.map(s => s.peerId)]);
  
  return (
    <>
      {Array.from(allPeers).map((peerId) => {
        // Use existing audio state or create a placeholder
        const audioState = audioStateMap.get(peerId) || {
          peerId,
          name: `Peer ${peerId.slice(0, 6)}`,
          volume: 1.0,
          distance: 0,
          muted: false,
          connected: true,
          isSelf: false,
        };
        
        return (
          <PeerAudio
            key={peerId}
            peerId={peerId}
            audioState={audioState}
            selectedOutputDeviceId={selectedOutputDeviceId}
            onStateChange={onPeerStateChange}
          />
        );
      })}
    </>
  );
}
